import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';

const ThemeTogglePill: React.FC = () => {
  const { setTheme, isDark } = useTheme();

  return (
    <div
      className={`flex items-center p-1 rounded-xl ${
        isDark ? 'bg-slate-900/80' : 'bg-slate-100'
      }`}
    >
      <button
        onClick={() => setTheme('light')}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex-1 justify-center ${
          !isDark
            ? 'bg-white text-slate-900 shadow-sm'
            : 'text-slate-400 hover:text-slate-300'
        }`}
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
        Light
      </button>
      <button
        onClick={() => setTheme('dark')}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex-1 justify-center ${
          isDark
            ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg shadow-violet-500/25'
            : 'text-slate-500 hover:text-slate-600'
        }`}
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
          />
        </svg>
        Dark
      </button>
    </div>
  );
};

export interface NavItem {
  name: string;
  href: string;
  icon: React.ReactNode;
  current: boolean;
  badge?: number;
}

export interface NavSection {
  title: string;
  items: NavItem[];
}

interface DashboardLayoutProps {
  children: React.ReactNode;
  sections: NavSection[];
  showThemeToggle?: boolean;
  showComplianceBadge?: boolean;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
  sections,
  showThemeToggle = true,
  showComplianceBadge = false,
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* Sidebar */}
      <aside
        className={`hidden lg:flex ${sidebarCollapsed ? 'w-[68px]' : 'w-60'} transition-all duration-300 flex-col ${
          isDark
            ? 'bg-slate-900/80 border-r border-slate-700/50'
            : 'bg-white border-r border-slate-200'
        }`}
      >
        {/* Toggle Button */}
        <div
          className={`p-3 border-b ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}
        >
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`w-full flex items-center justify-center p-2 rounded-lg transition-all ${
              isDark
                ? 'text-slate-500 hover:bg-slate-900/50 hover:text-slate-300'
                : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
            }`}
          >
            <span
              className={`transition-transform duration-300 ${sidebarCollapsed ? 'rotate-180' : ''}`}
            >
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </span>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 overflow-y-auto hide-scrollbar">
          {sections.map((section, sectionIdx) => (
            <div key={section.title} className={sectionIdx > 0 ? 'mt-6' : ''}>
              {!sidebarCollapsed && (
                <h3
                  className={`px-3 mb-2 text-[11px] font-medium uppercase tracking-wide ${
                    isDark ? 'text-slate-500' : 'text-slate-400'
                  }`}
                >
                  {section.title}
                </h3>
              )}
              {sidebarCollapsed && sectionIdx > 0 && (
                <div
                  className={`border-t mb-3 mx-3 ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}
                />
              )}
              <ul className="space-y-1">
                {section.items.map((item) => (
                  <li key={item.href} className="relative group">
                    <Link
                      to={item.href}
                      className={`
                        flex items-center gap-3 px-3 py-2.5 rounded-lg
                        text-sm font-medium transition-all duration-200
                        ${
                          item.current
                            ? isDark
                              ? 'bg-indigo-500/10 text-indigo-600'
                              : 'bg-indigo-50 text-indigo-600'
                            : isDark
                              ? 'text-slate-400 hover:bg-slate-900/50 hover:text-slate-200'
                              : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                        }
                        ${sidebarCollapsed ? 'justify-center' : ''}
                      `}
                    >
                      <div
                        className={`relative flex-shrink-0 ${item.current ? (isDark ? 'text-indigo-600' : 'text-indigo-600') : ''}`}
                      >
                        {item.icon}
                        {item.badge && (
                          <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-indigo-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                            {item.badge}
                          </span>
                        )}
                      </div>
                      {!sidebarCollapsed && <span>{item.name}</span>}

                      {/* Active indicator */}
                      {item.current && !sidebarCollapsed && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-5 bg-indigo-500 rounded-r-full" />
                      )}
                    </Link>

                    {/* Tooltip for collapsed state */}
                    {sidebarCollapsed && (
                      <div
                        className={`absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2.5 py-1.5 text-xs font-medium rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 ${
                          isDark
                            ? 'bg-slate-900 text-slate-200 border border-slate-700'
                            : 'bg-slate-900 text-white'
                        }`}
                      >
                        {item.name}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Theme Toggle */}
        {showThemeToggle && !sidebarCollapsed && (
          <div
            className={`px-3 py-3 border-t ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}
          >
            <ThemeTogglePill />
          </div>
        )}

        {/* Compliance Badge */}
        {showComplianceBadge && (
          <div
            className={`p-3 border-t ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}
          >
            <div
              className={`${sidebarCollapsed ? 'p-2 justify-center' : 'px-3 py-2.5'} rounded-lg flex items-center gap-3 ${
                isDark ? 'bg-slate-900/50' : 'bg-slate-50'
              }`}
            >
              <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-4 h-4 text-indigo-600"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              {!sidebarCollapsed && (
                <div className="min-w-0">
                  <div
                    className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                  >
                    SR&ED / CDAE
                  </div>
                  <div
                    className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                  >
                    {t('manager.complianceActive')}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">{children}</main>
    </div>
  );
};

export default DashboardLayout;
